/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';

import type { ShopperSearch } from '@/scapi';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Plus, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
import type { FilterValue, RefinementProps } from './types';
import RefineDefault from './refine-default';
import RefineColor from './refine-color';
import RefineSize from './refine-size';
import RefineCgid from './refine-cgid';
import RefinePrice from './refine-price';
import { colorValueRows, exclusiveAccordion, injectedGroupSlot, orderFacetGroups } from './group-order';
import { useFacetDrilldown, useFacetMobilePane, type FacetDrilldown } from './drilldown';
import { labelRefinementValues } from './value-labels';
import ActiveFilters from './active-filters';
import { useBrand } from '@/lib/brand/use-brand';
// @sfdc-extension-line SFDC_EXT_BOPIS
import RefineInventory from '@/extensions/bopis/components/refine-inventory';
// @sfdc-extension-line SFDC_EXT_BOPIS
import { STORE_AVAILABILITY_REFINEMENT } from '@/extensions/bopis/constants';
import { cn } from '@/lib/utils';

export default function CategoryRefinements({
    result,
    refine = [],
    categoryFacetFirst = false,
    allResultsRow = false,
    variant = 'card',
    surface = 'plp',
}: {
    result: ShopperSearch.schemas['ProductSearchResult'];
    refine: string[];
    /**
     * Render the `cgid` refinement as the FIRST group, expanded, with
     * per-option counts (search-3a-comparison.md P4 — live NM's rail leads
     * with Category). Default off: the PLP keeps cgid in QuickFilters.
     */
    categoryFacetFirst?: boolean;
    /**
     * Open the `cgid` group with an `All Results (N)` row carrying the query's
     * own total — the state where no category is selected, and the control that
     * clears one. Threaded from `SearchUx.railAllResultsRow`, which is the field
     * that holds the evidence: BG's desktop rail leads with it
     * (bg-search-d-1024, bg-search-d-1440, both with the group expanded) and no
     * NM capture contains the string at all.
     *
     * `SearchUx` is read only by `routes/_app.search.tsx`, so this reaches BG
     * SEARCH and nothing else — the PLP has its own `PLP_UX_BY_BRAND` map and
     * never passes it. Default off keeps every other mount unchanged.
     */
    allResultsRow?: boolean;
    /**
     * Visual variant: `card` is the stock bordered-accordion look (PLP + BG);
     * `rail` is the NM SRP left rail (blast-3: 47px group headers, 1px
     * #D3D3D3 dividers, 14px type — --facet-* tokens, tokens/search.css).
     */
    variant?: 'card' | 'rail';
    /**
     * WHICH SURFACE THIS RAIL IS MOUNTED ON. Live's category group is not one
     * treatment with brand skins — it differs by surface, in ways no brand key
     * can express, so the mounting route states it rather than the component
     * guessing from the URL:
     *
     *   per-option counts  BG search only. Absent from BG's PLP, and from NM's
     *                      PLP and SRP alike.
     *   bounded value list BG search only: the value list scrolls under a fixed
     *                      cap. Neither PLP capture caps any facet list.
     *
     * Both are derived in `refine-cgid`, each beside the bounded capture reads
     * behind it. Default `plp` because the PLP is the mount that passes no
     * `SearchUx` at all.
     */
    surface?: 'plp' | 'search';
}): ReactElement {
    const { t } = useTranslation();
    const brand = useBrand();
    const { railRef, railPinned } = usePinnedRail();
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const isPending = navigation.state !== 'idle';

    /**
     * Optimistic refinements derived from the in-flight navigation target.
     *
     * When a user toggles a filter, `navigate()` updates the URL which triggers a loader call. While that navigation
     * is pending, `navigation.location` holds the target location, allowing us to read the intended refine params
     * immediately.
     *
     * Note: `navigation.location` is inherently tied to the pending state — it's only defined while
     * `navigation.state !== 'idle'`, and both reset in the same render cycle once the navigation completes. An
     * additional `isPending` guard is therefore not needed here.
     *
     * Minor trade-off: `useNavigation()` is global — it reflects any in-flight navigation, not just filter toggles.
     * In practice this is acceptable because if a different navigation starts, the entire page is about to change
     * anyway.
     */
    const effectiveRefines = navigation.location
        ? new URLSearchParams(navigation.location.search).getAll('refine')
        : refine;

    // Track which sections should be expanded (default open if has active filters)
    const hasActiveFilter = useCallback(
        (attributeId: string) => {
            return effectiveRefines.some((r) => r.startsWith(`${attributeId}=`));
        },
        [effectiveRefines]
    );

    /**
     * The parenthesised figure live puts beside a group's name once something
     * in it is selected — `Gender (1)`.
     *
     * IT COUNTS SELECTED VALUES, not hits and not available values. Two
     * independent live sources, different brands and different numbers, which
     * is what rules out the other two readings:
     *
     *   NM desktop rail, owner capture filters-5-49-14PM (1728 CSS), three
     *   filters one per group -> `Women's Type (1)`, `Gender (1)`, `Color (1)`.
     *   Colour's own expanded list shows Black selected and Pink not, so a
     *   value count would read (2) and a hit count would read neither.
     *
     *   BG filter pane, owner capture filters-5-53-36PM (400 CSS), four filters
     *   as TWO sizes and TWO prices -> `Size (2)`, `Price (2)`. A brand that
     *   always printed (1) would fail here.
     *
     * Groups with nothing selected carry no parenthesis at all on either
     * source — hence the falsy check at the render site rather than `(0)`.
     */
    const selectedValueCount = useCallback(
        (attributeId: string) => {
            /* `cgid` NEVER counts. The category being browsed is carried in
               `refine` for behaviour and clear-all, so counting it printed
               `Category (1)` on every category page — measured on both brands
               before this guard. Live prints `Category` bare in exactly that
               state: NM's filters-5-49-01PM is a category page with a filter
               applied and its Category heading carries no parenthesis, and BG's
               filter pane lists `Category` bare beside `Size (2)`. This is the
               same exclusion `active-filters` already makes, for the same
               reason, and the two must agree — a chip and a heading count that
               disagree about what is selected is worse than neither. */
            if (attributeId === 'cgid') return 0;
            return effectiveRefines.filter((r) => r.startsWith(`${attributeId}=`)).length;
        },
        [effectiveRefines]
    );

    // Category (`cgid`) selection is handled by QuickFilters on the PLP; the NM
    // SRP opts it INTO the rail as the leading group instead (categoryFacetFirst).
    //
    // Everything BELOW cgid is ordered by `group-order.ts` rather than by the
    // order SCAPI returned. The response puts Price THIRD of five on every
    // brand-by-surface combination and live puts it last or second-to-last, so
    // rendering the response verbatim shipped the same wrong order four times
    // over; the citations for live's order, and for why BG's PLP alone
    // transposes Price and Color, are in that file.
    const refinements = useMemo(() => {
        // The store-availability refinement is dropped from the GENERIC facet list on
        // purpose, and dropped here rather than per-arm so the drilldown and the
        // accordion both inherit it. Its values are raw store ids — SCAPI offers it as
        // 33 numeric values on NM and 2 on BG, with hit counts — which would paint a
        // facet row of bare numbers that means nothing to a shopper. The same
        // refinement is driven, with the selected store's id as its value and that
        // store's NAME as its label, by `RefineInventory` further down this file.
        // @sfdc-extension-line SFDC_EXT_BOPIS
        const all = (result?.refinements || []).filter(
            (refinement) => refinement.attributeId !== STORE_AVAILABILITY_REFINEMENT
        );
        const rest = orderFacetGroups(
            all.filter((refinement) => refinement.attributeId !== 'cgid'),
            brand,
            surface
        );
        if (!categoryFacetFirst) {
            return rest;
        }
        const cgid = all.filter((refinement) => refinement.attributeId === 'cgid');
        return [...cgid, ...rest];
    }, [result, categoryFacetFirst, brand, surface]);

    /**
     * DOES ANY SCAPI GROUP ACTUALLY PAINT? Not "did the response carry any" —
     * the two answers differ, and the gap between them is a real defect.
     *
     * On a query that returns nothing, SCAPI still sends refinement ENTRIES; it
     * sends them with empty `values`. Every block below drops such an entry
     * through its own `values.length` guard, so the rail renders zero groups
     * while `refinements.length` is nonzero — which walks straight past the
     * `refinements.length === 0` early return above, the one place that already
     * knew not to paint an injected control with nothing beside it.
     *
     * Measured on the running app 2026-08-23, both brands, 1280 and 1728, by
     * enumerating the rail container's DIRECT CHILDREN: `?q=dress` gives 8
     * children (7 refinement sections plus this group); a zero-result query
     * gives ONE, this group alone, with the early return's message absent —
     * proving the main return ran and every section self-suppressed. So the
     * facet rail became a single store-pickup control offering to filter a set
     * with no members, on a page whose own copy says there are no results. On
     * BG that control is `[ours-extra]` besides, so the rail was made entirely
     * of something live does not have.
     *
     * This corrects the EXISTING guard's predicate rather than adding a new
     * suppression: the rule is still "no groups, no injected control", it just
     * counts groups that render instead of array entries. It degrades correctly
     * in the direction that matters — any facet with values, including the
     * Gender group being authored into the catalogue now, makes this true and
     * restores the previous behaviour exactly.
     */
    const railHasRenderableGroup = useMemo(
        () => refinements.some((refinement) => Array.isArray(refinement.values) && refinement.values.length > 0),
        [refinements]
    );

    // Where the injected Get it Fast group sits relative to the SCAPI groups —
    // or `'none'`, which is BG, where live draws no such facet. Live citations
    // are on `INJECTED_GROUP_SLOT` in `group-order.ts`.
    const inventorySlot = injectedGroupSlot(brand);

    /**
     * TWO-LEVEL DRILL-DOWN, WHEN — AND ONLY WHEN — A PANE PROVIDES ONE.
     *
     * `useFacetDrilldown()` returns `null` on both desktop rails and inside NM's
     * mobile pane, because nothing there mounts the provider. The whole feature
     * is therefore gated on a value this component receives rather than on a
     * brand it looks up, and every unprovided mount takes the identical code
     * path it took before the drill-down existed.
     *
     * IT ALSO DECLINES WHEN THE RAIL CARRIES AN INJECTED GROUP, and that guard is
     * the point rather than defensive noise. `RefineInventory` is not a
     * `FilterSection`: it renders its own `Collapsible` over private state and
     * has no group VALUES to put on a second screen, so it cannot become a row
     * that navigates. A drill-down containing it would ship rows that navigate
     * beside one that expands — which is precisely why the previous pass on this
     * pane declined to build the feature at all.
     *
     * Today no mount reaches that combination: the only provider is BG's pane,
     * and BG's slot is `'none'`. The guard exists so the failure mode of a future
     * mount is FALLING BACK TO THE ACCORDION — correct everywhere, merely not
     * live's shape — rather than silently dropping a group from the list. A
     * mechanism that degrades to the previous behaviour beats one that is right
     * only while an assumption holds.
     */
    const drilldown = useFacetDrilldown();
    const drilldownActive = drilldown !== null && inventorySlot === 'none';

    /**
     * NOTHING IS OPEN ON FIRST PAINT INSIDE A MOBILE PANE. Live's desktop rails
     * open Category; live's mobile panes open nothing. Evidence for both sides on
     * `FacetMobilePane` in `./drilldown`.
     *
     * Reported by the owner, 2026-08-23, against BOTH brands: *"live opens with
     * no filter options open, we have categories open. same on bg actually."*
     *
     * It reaches exactly two decisions — the unconditional `defaultOpen` on the
     * leading Category block, and the exclusive accordion's initial group. It
     * does NOT touch `defaultOpen={hasActiveFilter(...)}` on the other groups: a
     * group opening because it carries an applied refinement is a different
     * behaviour from a group opening by default, live's pane in that state is
     * unmeasured, and the report was about a pane with nothing applied.
     */
    const collapseAllByDefault = useFacetMobilePane()?.collapseAllByDefault ?? false;

    /**
     * The rows the drill-down list draws, in the order the rail would have drawn
     * the groups. `cgid` is included as an ordinary row when the route leads with
     * Category — live BG's own list carries `Category` as its first row, with the
     * same chevron as every other — and dropped otherwise, exactly as the
     * accordion arm drops it.
     */
    const drilldownGroups = useMemo(
        () =>
            refinements.filter(
                (refinement) =>
                    (categoryFacetFirst || refinement.attributeId !== 'cgid') &&
                    Array.isArray(refinement.values) &&
                    refinement.values.length > 0
            ),
        [refinements, categoryFacetFirst]
    );

    /**
     * EXCLUSIVE ACCORDION — one open group at a time, BG only.
     *
     * The evidence, and why NM is deliberately left multi-open, is on
     * `EXCLUSIVE_ACCORDION` in `group-order.ts`.
     *
     * THE STATE LIVES HERE RATHER THAN IN `FilterSection` because exclusivity is
     * a property of the SET, not of a section: a section cannot know that a
     * sibling just opened. `FilterSection` keeps its own uncontrolled state and
     * gains an optional controlled pair, so the brand that does not ask for this
     * takes the identical code path it took before — inert by construction
     * rather than by a flag read at the bottom of the component.
     *
     * The initial value reproduces exactly what `defaultOpen` used to resolve to,
     * evaluated ONCE the way `useState(defaultOpen)` already did: `cgid` when the
     * route leads with Category, otherwise the first group carrying an active
     * refinement, otherwise nothing. Two active refinements on different groups
     * previously opened both; under exclusivity the FIRST in live's own group
     * order wins, because `refinements` is already sorted by `orderFacetGroups`.
     * Which one live picks in that state is unmeasured — no capture on disk has
     * two groups refined at once — so this is the reading that changes the fewest
     * pixels, not a claim about live.
     */
    const exclusive = exclusiveAccordion(brand);
    const [openGroup, setOpenGroup] = useState<string | null>(() => {
        // Inside a pane, Category does not lead OPEN — it leads CLOSED like every
        // other group. A group carrying an applied refinement still wins below,
        // which is the deliberately narrow reading of the report.
        if (!collapseAllByDefault && categoryFacetFirst && refinements.some((r) => r.attributeId === 'cgid')) {
            return 'cgid';
        }
        return refinements.find((r) => r.attributeId && hasActiveFilter(r.attributeId))?.attributeId ?? null;
    });

    /**
     * The controlled pair a `FilterSection` gets under exclusivity, and NOTHING
     * at all otherwise — `open: undefined` is what puts the section back on its
     * own uncontrolled state, so this must spread empty rather than pass
     * `open={false}`.
     */
    const exclusiveProps = (attributeId: string): { open?: boolean; onOpenChange?: (next: boolean) => void } =>
        exclusive
            ? {
                  open: openGroup === attributeId,
                  onOpenChange: (next: boolean) => setOpenGroup(next ? attributeId : null),
              }
            : {};

    const toggleFilter = useCallback(
        (attributeId: string, value: string) => {
            const params = new URLSearchParams(location.search);
            const refines = params.getAll('refine');
            const refinePair = `${attributeId}=${value}`;

            let nextRefines: string[];
            if (refines.includes(refinePair)) {
                // Remove this refinement
                nextRefines = refines.filter((r) => r !== refinePair);
            } else {
                /* EXCLUSIVE REFINEMENTS — ONE VALUE AT A TIME. This is not a
                   UX preference and it is not a parity gap. DO NOT "FIX" IT BY
                   ALLOWING MULTI-SELECT.

                   `price` is here because SCAPI cannot express a price union.
                   Measured against the running sandbox: the OR form
                   `price=(50..150)|(150..300)` returns HTTP 400 "Malformed
                   Price Refinement", and repeating the parameter does not union
                   — the last one silently wins and the response describes only
                   that tier. So a rail that let two tiers look selected would
                   render two chips over a result set that honours one. The
                   failure would be invisible: a plausible grid, a plausible
                   count, and a chip list that lies about how it was produced.

                   LIVE BG *DOES* HOLD TWO PRICE TIERS AT ONCE, and that is not
                   a counter-argument. Its own saved markup carries
                   `Price: $150-$200` and `Price: $100-$150` as two chips
                   (live-shots/owner-2026-08-23-filters/bg-filters-dt.html).
                   Live BG is the legacy NMG stack, not SCAPI; the capability
                   difference is in the backend, not in this component. Closing
                   it means a backend that supports price unions, not a change
                   here.

                   The store-availability id is exclusive for a different and
                   simpler reason: the value IS a store id, and a shopper is in
                   one store at a time. */
                const exclusiveRefinements = [
                    'price',
                    // @sfdc-extension-line SFDC_EXT_BOPIS
                    STORE_AVAILABILITY_REFINEMENT,
                ];
                if (exclusiveRefinements.includes(attributeId)) {
                    // Remove all refinements for this attribute first
                    nextRefines = [...refines.filter((r) => !r.startsWith(`${attributeId}=`)), refinePair];
                } else {
                    nextRefines = [...refines, refinePair];
                }
            }

            // Rebuild search params with the new refines
            params.delete('refine');
            nextRefines.forEach((r) => params.append('refine', r));
            params.set('offset', '0');

            const nextSearch = `?${params.toString()}`;

            void navigate({
                pathname: location.pathname,
                search: nextSearch,
            });
        },
        [location, navigate]
    );

    /**
     * Drop every refinement for one attribute in a single navigation.
     *
     * `toggleFilter` cannot express this: it flips ONE pair, so clearing an
     * attribute that holds several would take several navigations. Live BG's
     * `All Results` row is a "no category" state rather than a value, which is
     * what needs it.
     */
    const clearFilter = useCallback(
        (attributeId: string) => {
            const params = new URLSearchParams(location.search);
            const kept = params.getAll('refine').filter((r) => !r.startsWith(`${attributeId}=`));
            params.delete('refine');
            kept.forEach((r) => params.append('refine', r));
            params.set('offset', '0');
            // Both paging params, for the same reason the BG tab row resets both.
            params.delete('page');

            void navigate({ pathname: location.pathname, search: `?${params.toString()}` });
        },
        [location, navigate]
    );

    // Check if a filter value is selected (uses optimistic state)
    const isFilterSelected = useCallback(
        (attributeId: string, value: string) => {
            return effectiveRefines.includes(`${attributeId}=${value}`);
        },
        [effectiveRefines]
    );

    // Render the appropriate filter component based on type
    const renderFilterValues = (
        refinement: ShopperSearch.schemas['ProductSearchRefinement'] & { values: FilterValue[] }
    ) => {
        const { attributeId } = refinement;
        // Groups whose refinement definition carries no value display names
        // arrive with `label` echoing the raw wire value — `c_isSale` shipped
        // `true`/`false` onto the rail this way. Inert for every other group.
        const values = labelRefinementValues(attributeId, refinement.values, t);
        const refinementProps: RefinementProps = {
            values,
            attributeId,
            isFilterSelected,
            toggleFilter,
            variant,
        };

        switch (attributeId) {
            case 'cgid':
                // Live NM's Category options are links with counts, not
                // checkboxes (search-3a-comparison.md "Facets + sort").
                return (
                    <RefineCgid
                        {...refinementProps}
                        surface={surface}
                        allResultsRow={allResultsRow}
                        // The query's own total, which is the figure live puts
                        // in the row: `All Results  (3749)` against
                        // `Women's Clothing (3227)` on the same rail
                        // (bg-search-d-1024/App.tsx).
                        allResultsCount={result?.total}
                        anyCategorySelected={hasActiveFilter(attributeId)}
                        onClearCategory={() => clearFilter(attributeId)}
                    />
                );
            // THE COLOUR CASE IS BACK, AND IT IS NOT THE DELETED RENDERER.
            // What lands here is live's ROW STRUCTURE — one value per row, bold
            // label on the selected row, no hit count — and explicitly NOT a
            // colour disc, because the catalogue carries no colour for a
            // refinement bucket. Routes 1 and 2 were both probed against the
            // live instance on 2026-08-23 and both refuted; the evidence, and
            // what would unblock the disc, is in `refine-color`.
            //
            // It is gated on BRAND rather than on the `rail` variant, because
            // `variant="rail"` also reaches BG's mobile overlay — see
            // `colorValueRows`. BG keeps the default renderer: the only live BG
            // filter capture on disk has its Color group collapsed, so BG's
            // colour rows are `unmeasured`.
            //
            // The DELETED renderer is still deleted and should stay that way. It
            // drew a multi-column grid of 32px squares with labels centred
            // underneath, which matches no live source on either brand at any
            // width. It was also keyed on `c_refinementColor` and so never once
            // ran, because SCAPI does not send that id as a refinement
            // attributeId on any route. Measured against the running app on
            // 2026-08-22, four route payloads, `c_color` as the positive
            // control in the same command:
            //
            //   NM search  c_refinementColor 0   c_color 1
            //   BG search  c_refinementColor 0   c_color 1
            //   NM PLP     c_refinementColor 0   c_color 1
            //   BG PLP     c_refinementColor 0   c_color 1
            //
            // Colour therefore falls to the default vertical list below, which
            // is the shape both brands paint live — one value per row. The
            // deleted renderer drew a multi-column grid of 32px squares with
            // labels centred underneath, which matches no live source on either
            // brand at any width, so wiring it to `c_color` would have been a
            // regression rather than a fix.
            //
            // `c_refinementColor` still appears throughout the product mocks as
            // a PRODUCT attribute holding a colour NAME, alongside `c_color`
            // holding a colour CODE. They are different attributes. Do not
            // sweep the identifier.
            case 'c_color':
                return colorValueRows(brand) ? (
                    <RefineColor {...refinementProps} />
                ) : (
                    <RefineDefault {...refinementProps} />
                );
            case 'c_size':
                return <RefineSize {...refinementProps} />;
            case 'price':
                return <RefinePrice {...refinementProps} result={result} />;
            // THE DESIGNER GROUP IS `brand`, NOT `c_brand`. Measured on the
            // running app at 1280, reading the generated checkbox ids off the
            // expanded group on four route payloads — every one reports the
            // plain, un-prefixed id:
            //
            //   NM PLP     brand   19 values   A.L.C. .. Theia
            //   NM search  brand   15 values   A.L.C. .. Theia
            //   BG PLP     brand   20 values   A.L.C. .. Zimmermann
            //   BG search  brand    6 values   A.L.C. .. Zimmermann
            //
            // `c_brand` is a PRODUCT attribute — it appears in this package
            // only inside `select=` query documentation — and SCAPI does not
            // send it as a refinement attributeId on any route. Casing on it
            // would produce a branch that never runs, which is exactly how the
            // deleted colour renderer sat unreachable behind
            // `c_refinementColor`. Do not sweep the identifier.
            //
            // The searchable flag is set HERE rather than inside the fallback
            // renderer because that renderer also draws Availability, Sale,
            // Gender and BG's Color, none of which is a live source for a
            // type-ahead. The dispatcher is the surface that already encodes
            // per-facet treatment, so the knowledge belongs on this line. The
            // renderer applies its own list-length gate on top — see the
            // constant there for why an input over six values is not parity.
            case 'brand':
                return <RefineDefault {...refinementProps} searchable />;
            default:
                return <RefineDefault {...refinementProps} />;
        }
    };

    // No refinements available
    if (refinements.length === 0) {
        return (
            <div className="border p-4" data-testid="category-refinements">
                <p className="type-body text-muted-foreground">{t('categoryRefinements:noFilterOptionsAvailable')}</p>
            </div>
        );
    }

    if (drilldownActive) {
        return (
            <UITarget targetId="sfcc.plp.search.filters">
                <div data-testid="category-refinements" data-variant={variant} data-layout="drilldown">
                    <FacetDrilldownBody
                        drilldown={drilldown}
                        groups={drilldownGroups}
                        renderFilterValues={renderFilterValues}
                    />
                </div>
            </UITarget>
        );
    }

    return (
        <UITarget targetId="sfcc.plp.search.filters">
            <div
                ref={railRef}
                data-testid="category-refinements"
                data-variant={variant}
                // No `data-rail-pinned` hook alongside this. An attribute we set
                // and then assert on is a vacuous check — it passes whether or
                // not the sticky actually engages — and nothing that paints
                // would read it, which `src/__tests__/declared-never-read` is
                // there to catch. The emitted style below IS the observable the
                // browser acts on; the behaviour itself is verified by scrolling.
                style={
                    railPinned
                        ? {
                              position: 'sticky',
                              top: 'var(--facet-rail-sticky-offset, var(--header-height, 0px))',
                              maxHeight: 'calc(100vh - var(--facet-rail-sticky-offset, var(--header-height, 0px)))',
                              overflowY: 'auto',
                          }
                        : undefined
                }
                className={cn(
                    variant === 'rail' && 'px-(--facet-rail-pad-x)',
                    isPending && 'pointer-events-none opacity-50 transition-opacity'
                )}>
                {/* SELECTED FILTERS — the box lives at the TOP OF THE RAIL,
                    above the first group, on BOTH brands. Owner captures of
                    2026-08-23: `filters-5-49-01PM` (live NM, 1702 CSS, one
                    filter) and `filters-5-49-14PM` (live NM, 1728, three) put a
                    bordered box above `Category`; `filters-5-53-11PM` (live BG,
                    1728, four) does the same, and BG's saved markup nests
                    `div.selected-filters` inside `div.facets-container` ahead of
                    the first `rc-collapse` group.

                    Ours had it in the RESULTS COLUMN instead, above the grid —
                    a whole band in the wrong place, which no value table would
                    have surfaced. The route mounts stay put and self-suppress
                    at `md` and up; below `md` the rail is `hidden` and they are
                    still the only chip UI. Rendering it here rather than moving
                    the route mounts keeps the change inside this component:
                    both route files belong to other work in flight. */}
                <ActiveFilters result={result} placement="rail" />
                {/* NM rail order (search-3a-comparison.md P4): Category leads,
                    then the SCAPI facets in LIVE's order — see `group-order.ts`,
                    which supersedes the response order this block used to
                    render — and the injected availability group last; see the
                    note on that block below for why it is last rather than
                    second. */}
                {categoryFacetFirst &&
                    refinements
                        .filter((refinement) => refinement.attributeId === 'cgid')
                        .map((refinement) => {
                            const { values, attributeId, label } = refinement;
                            if (!Array.isArray(values) || !values.length) {
                                return null;
                            }
                            return (
                                <FilterSection
                                    key={attributeId}
                                    label={label || attributeId}
                                    selectedCount={selectedValueCount(attributeId)}
                                    variant={variant}
                                    /* Open on the DESKTOP rails, which is what
                                       live does on both brands; closed inside a
                                       mobile pane, which is also what live does.
                                       See `collapseAllByDefault` above. */
                                    defaultOpen={!collapseAllByDefault}
                                    {...exclusiveProps(attributeId)}>
                                    {renderFilterValues(
                                        refinement as ShopperSearch.schemas['ProductSearchRefinement'] & {
                                            values: FilterValue[];
                                        }
                                    )}
                                </FilterSection>
                            );
                        })}

                {/* GET IT FAST, IN LIVE NM'S SLOT: immediately after Category
                    and before every SCAPI group. Live NM puts it there on both
                    of its listing surfaces — re-derived from live's own markup
                    with a DOM engine, citations and controls on
                    `INJECTED_GROUP_SLOT` in `group-order.ts`.

                    IT SITS BETWEEN TWO BLOCKS RATHER THAN AT AN INDEX. Nothing
                    here counts groups or names a position, so a facet arriving
                    in the payload — Gender is being authored now — lands in the
                    block below and cannot displace this. A facet disappearing
                    is the same non-event.

                    BG renders the `'last'` arm further down instead, unchanged
                    from what it has always done, because live BG has no
                    store-pickup facet to take a position from. */}
                {/*  @sfdc-extension-block-start SFDC_EXT_BOPIS */}
                {inventorySlot === 'after-category' && railHasRenderableGroup && (
                    <RefineInventory
                        isFilterSelected={isFilterSelected}
                        hasActiveFilter={hasActiveFilter}
                        toggleFilter={toggleFilter}
                        variant={variant}
                    />
                )}
                {/*  @sfdc-extension-block-end SFDC_EXT_BOPIS */}

                {/* Individual collapsible sections for each refinement category */}
                {refinements
                    .filter((refinement) => refinement.attributeId !== 'cgid')
                    .map((refinement) => {
                        const { values, attributeId, label } = refinement;
                        if (!Array.isArray(values) || !values.length) {
                            return null;
                        }

                        return (
                            <FilterSection
                                key={attributeId}
                                label={label || attributeId}
                                selectedCount={selectedValueCount(attributeId)}
                                variant={variant}
                                defaultOpen={hasActiveFilter(attributeId)}
                                {...exclusiveProps(attributeId)}>
                                {renderFilterValues(
                                    refinement as ShopperSearch.schemas['ProductSearchRefinement'] & {
                                        values: FilterValue[];
                                    }
                                )}
                            </FilterSection>
                        );
                    })}

                {/* AVAILABILITY GOES LAST, and it is the one group in this rail
                    whose position is ours to choose: it is not a SCAPI
                    refinement at all but a store-inventory control this file
                    injects, so nothing in the response constrains where it
                    sits. It used to be injected between the cgid block and the
                    rest, which put it 2nd of 6 on all four brand-by-route
                    combinations and split the Category-to-Designer adjacency
                    that both brands keep on live.

                    LIVE PUTS IT AFTER PRICE, EVERYWHERE IT APPEARS. Counted off
                    the raw dumps, whose group lists are bounded by their own
                    container — BG: the single `div.rc-collapse`, children
                    `div.rc-collapse-item`; NM: `#faceted-left-nav`, children
                    `div.faceted-left-nav__filter` plus the separate Category
                    control. Nested category rows (`react-collapsed-toggle-N`)
                    are children of Category, not groups, and are excluded.

                      BG search   Category, Designer, Size, Color, Price,
                                  AVAILABILITY, Sale, Gender  -> 6th of 8
                                  (dumps/bg-search-d-1280, items at bytes
                                  166556..171864; identical at m-440. Also the
                                  owner's 1728 browser shot of
                                  bergdorfgoodman.com/search/?N=0&Ntt=dress, and
                                  bg-search-facet-d-1024 role="tab" order)
                      NM search   Category, Get it Fast, Gender, Designer, Size,
                                  Color, Price, AVAILABILITY, Sale -> 7th of 9
                                  (dumps/nm-search-d-1280, group buttons at
                                  bytes 271968..282389)
                      NM PLP      Category, Get it Fast, Designer, Size, Color,
                                  Price, Sale, Type, Occasion, AVAILABILITY
                                  -> 10th of 10, last
                                  (dumps/nm-plp-d-1280, 277612..288300)
                      BG PLP      PER-CATEGORY. Live BG's PLP rail carries it on
                                  some categories and not others, so there is no
                                  single answer for the surface:
                                    womens-clothing-cat441206 — six groups,
                                      Category, Designer, Size, Price, Color,
                                      Sustainability Type, NO Availability
                                      (dumps/bg-plp-d-1280 container 182761 with
                                      children 182801..193175; bg-plp-m-440
                                      container 172856, 172896..183270; a third
                                      capture of the same category,
                                      bg-plp-d-1280-dup-older, agrees)
                                    womens-clothing-clothing-dresses-cat80001 —
                                      TWELVE groups, Category, Designer, Size,
                                      Price, Color, pattern, Dress Length,
                                      Style, Sleeve Length, Material,
                                      AVAILABILITY, Sustainability Type
                                      -> 11th of 12, at 199833..200435 in
                                      .reference/bg-plp-womens-dresses.desktop.html
                                      (canonical URL in the file; the group is a
                                      real collapsed rc-collapse-item with the
                                      header text Availability)

                    AN EARLIER REVISION OF THIS COMMENT SAID "ABSENT ENTIRELY"
                    AND CALLED AVAILABILITY PER-SURFACE ON BG. That was a
                    population error, not a counting error: the count was
                    container-bounded and correct, but every BG PLP capture it
                    drew on was the SAME CATEGORY. BG's facet set is
                    server-driven per category — the dresses capture proves it,
                    and it carries five groups womens-clothing does not. One
                    category cannot establish a never for the surface.

                    So a BG-PLP-wide suppression of this group would be wrong:
                    it would remove a control live renders on dresses in order
                    to match live on womens-clothing. If this is ever revisited,
                    the coherent unit is the CATEGORY's own facet set, which
                    means the response would have to drive it — and SCAPI does
                    not return this facet on either brand, which is exactly why
                    this group is injected here in the first place.

                    An earlier count read NM search as "7th of 8" and NM PLP as
                    "9th of 9". Both dropped Category, which NM renders through
                    `button.search-results__cat-button` rather than a
                    `data-tid="filter-key-*"` button and which a filter-key
                    enumeration therefore cannot see.

                    THE PREMISE THIS BLOCK RESTED ON IS DEAD, AND EVERYTHING
                    ABOVE ABOUT "LIVE'S AVAILABILITY SLOT" IS ABOUT A DIFFERENT
                    GROUP THAN THE ONE BELOW. It used to read: "We render six
                    groups (Sale and Gender are a sandbox data gap, not a
                    layout one), so last here IS live's slot." Both halves have
                    since gone false, and neither went false through a code
                    change — the SANDBOX DATA moved underneath them.

                      the data gap closed   `c_isSale` and `c_preOrder` now come
                                            back as refinements. Measured on the
                                            running app 2026-08-23 at 1280 by
                                            enumerating the rail container's
                                            DIRECT CHILDREN on five rails (BG
                                            search / handbags / dresses, NM
                                            search / womens-clothing): SEVEN
                                            refinement sections render, Sale
                                            among them, not six.
                      the slot is not ours  `c_preOrder` IS live's Availability
                                            group — it renders live's own two
                                            values, `In-Stock` and `Preorder`.
                                            The group below is live's GET IT
                                            FAST, a different facet that live NM
                                            renders SECOND. See the corrected
                                            docstring on `RefineInventory`.

                    So for a while every rail printed the word "Availability"
                    twice — once on `c_preOrder`, once on the group below — and
                    no code review could have caught it, because no code
                    changed. The heading below is now "Get it Fast".

                    ITS POSITION IS BRAND-KEYED, AND NO BRAND REACHES THIS BLOCK
                    TODAY. NM renders the group from the `'after-category'` block
                    above, which is live NM's own slot. BG is `'none'` — live BG
                    draws no store-pickup facet under any name on any surface, so
                    the group was `[ours-extra]` here rather than misplaced, and
                    the honest slot for a group live does not have is no slot.
                    Three independent live sources and their controls are on
                    `INJECTED_GROUP_SLOT` in `group-order.ts`.

                    THE BLOCK STAYS, AND THAT IS THE POINT OF EXPRESSING THE SLOT
                    AS A RELATION. Restoring BG — if a capture ever shows the
                    facet — is one word in that map, with no JSX to re-derive and
                    no position to re-invent. Deleting it would trade a live
                    reversal for a rebuild.

                    THE "HOW DOES SCAPI ORDER THE FACETS" QUESTION THIS BLOCK
                    USED TO LEAVE OPEN IS NOW ANSWERED AND ACTED ON. The
                    response returns `cgid, brand, price, c_size, c_color` on
                    every brand-by-surface combination — measured by decoding
                    eight loader payloads off the running app — which put Price
                    third of five where live puts it last or second-to-last.
                    `group-order.ts` now imposes live's order instead, so the
                    ranked groups no longer depend on the response at all.
                    Availability still sorts after Price under that order on
                    every surface, so this block's own slot is unchanged by it. */}
                {/*  @sfdc-extension-block-start SFDC_EXT_BOPIS */}
                {inventorySlot === 'last' && railHasRenderableGroup && (
                    <RefineInventory
                        isFilterSelected={isFilterSelected}
                        hasActiveFilter={hasActiveFilter}
                        toggleFilter={toggleFilter}
                        variant={variant}
                    />
                )}
                {/*  @sfdc-extension-block-end SFDC_EXT_BOPIS */}
            </div>
        </UITarget>
    );
}

/**
 * Top-pinned facet rail, bounded to the viewport below the header and scrolling
 * internally — which is what BOTH live brands do. Measured 2026-08-21 off live's
 * own shipped production stylesheets and page bundles in `.reference/dumps/**`;
 * an at-rest capture cannot witness any of this, so the declarations are the
 * evidence and the earlier reading here was built on their absence.
 *
 * WHAT LIVE DECLARES — NM. The rail is `<nav id="faceted-left-nav">` wrapping
 * `<div id="faceted-left-nav__child">`; the child is the box that moves.
 *   .faceted-left-nav__child  { height:100%; overflow-y:auto; overflow-x:hidden;
 *                               transition:top .3s ease-in-out }
 *   .faceted-left-nav__fixed  { position:fixed; top:0; z-index:99;
 *                               background-color:#fff }
 *   .faceted-left-nav__child.shifted { top:175px }   (PLP bundle)
 *   .faceted-left-nav__child.shifted { top:130px }   (SRP bundle)
 * `__fixed` is added by the rail's own scroll handler once the nav's top edge
 * reaches the viewport top; `shifted` tracks live's reveal-on-scroll-up header,
 * so the offset alternates between the header's height and 0 as the header
 * shows and hides. There is NO `max-height` on the rail — under `position:fixed`
 * the `height:100%` resolves against the viewport, which is the same bound by a
 * different spelling. The only `max-height`s under `.faceted-left-nav` belong to
 * descendants (a generic 300px, and 500px on the size-facet's own scroller).
 *
 * WHAT LIVE DECLARES — BG. The rail is `<div id="sortFilterBar">` wrapping
 * `<div class="facets-container">`, inside `.left-nav-menu`.
 *   .sticky-filter-lists .filter-lists { position:fixed; top:0; left:0;
 *                                        width:100%; height:59px; z-index:3 }
 *   .left-nav-menu .sticky-filter-lists .filter-lists
 *                                      { box-sizing:border-box; height:unset;
 *                                        left:unset; width:unset;
 *                                        max-height:100vh; padding:8px 0 0 }
 * The second block is the left-rail specialisation of the horizontal sticky
 * filter bar: it keeps `position:fixed; top:0` and swaps the bar's fixed 59px
 * height for `max-height:100vh`. BG declares `overflow-y` nowhere — its scroll
 * handler writes it inline alongside `box-sizing`, `padding-right:10px` and
 * `scrollbar-gutter:stable`, which is why the served markup carries
 * `style="padding-right: 10px; overflow-y: auto; box-sizing: border-box;
 * scrollbar-gutter: stable; height: auto;"` on `.facets-container` at rest.
 *
 * SO THE MECHANISM IS ONE THING, NOT TWO. Both brands pin the rail's TOP and
 * bound its height to the viewport so it scrolls under its own cursor. "Top
 * pinned" and "clipped box with a scroll thumb" were never alternatives — one
 * is the positioning, the other is the box, and live does both. Neither brand
 * bottom-pins: no `bottom` is declared on either rail. Both use JS-toggled
 * `position:fixed` plus a spacer, which is the pre-`sticky` spelling of exactly
 * this; `position:sticky` reaches the same observable and gets the end-of-column
 * release for free, where live has to compute it (NM writes `bottom:<px>;
 * top:auto` inline once its recs/footer block reaches the rail's bottom edge, BG
 * writes `height:<#pageBottom top>px`).
 *
 * WHAT WE EMIT, and why each part:
 *   position: sticky
 *   top:        var(--facet-rail-sticky-offset, var(--header-height, 0px))
 *   max-height: calc(100vh - <that same offset>)
 *   overflow-y: auto
 * The offset defaults to OUR header rather than live's 175/130/0 literals,
 * because those are measurements of live's own header on a specific surface and
 * ours is a different height; `--header-height` is published on the document
 * element by `components/header` and is what `product-listing/sticky-bar.tsx`
 * already pins to for the same reason. `100vh` matches BG's declared unit; this
 * is a desktop layout, so the dvh distinction does not arise.
 *
 * WHY THE OFFSET IS INDIRECTED THROUGH A TOKEN, added 2026-08-22. The header
 * height is only the right offset while the header is actually PINNED there.
 * BG's is not: live BG's masthead scrolls away on search, PLP and PDP and
 * nothing re-pins, so `routes/_app.tsx` passes `sticky={false}` for that brand
 * and the header stops occupying the top of the viewport. Anchoring the rail to
 * the header's height regardless would park it a header-height below the
 * viewport top on every scrolled BG page — a permanent dead gap, and the whole
 * reason the two changes are one unit of work.
 *
 * The token is DECLARED ONLY BY BG (tokens/brand-bg/search.css). Nothing is
 * added to a shared `:root`, so NM and any unset brand fall through to the
 * fallback and re-assert exactly the value this line carried before — inert by
 * construction rather than by a neutral somebody had to invent. An inline style
 * is what the browser acts on here and inline styles cannot be overridden from
 * a stylesheet, which is why the brand arm has to arrive as a var rather than
 * as a competing rule.
 *
 * THE `max-height` IS NOT DECORATION. Without it a rail taller than the viewport
 * has no way to reach its own bottom entries except by scrolling the whole page,
 * and the previous offset here made that worse: it clamped to
 * `min(--header-height, 100vh - ownHeight)`, whose second arm goes NEGATIVE
 * exactly when the rail overflows, holding the rail's TOP above the viewport and
 * hiding its leading groups with no internal scroll to recover them.
 *
 * TRAVEL ROOM IS THE LAYOUT DISCRIMINATOR, deliberately measured rather than
 * gated on a breakpoint. Both routes mount this one element as a `w-full` panel
 * at mobile and a fixed-width flex column at `md`, so there is no second element
 * to gate. The rail's column is a flex item: in the desktop ROW layout it is
 * stretched to the grid's height (measured 2989.5px NM / 1768.9px BG at 1280
 * against rails of 411/443), and in the mobile COLUMN layout it is exactly as
 * tall as the rail. Zero room means a sticky offset can have no effect at all,
 * so nothing is emitted — no breakpoint constant to keep in sync with the one
 * the route uses, and a collapsed or hidden column falls out for free.
 *
 * Arming cannot oscillate. It requires `column > railHeight`, and the column is
 * the taller of the rail and the results grid — so arming implies the GRID is
 * the taller one, and capping the rail cannot take the column below the cap.
 */
function usePinnedRail(): { railRef: RefObject<HTMLDivElement | null>; railPinned: boolean } {
    const railRef = useRef<HTMLDivElement>(null);
    const [railPinned, setRailPinned] = useState(false);

    useEffect(() => {
        const rail = railRef.current;
        if (!rail) {
            return;
        }

        const measure = () => {
            const column = rail.parentElement;
            const height = rail.offsetHeight;
            // 1px of slack absorbs the sub-pixel column heights this layout
            // produces (2989.5, 1768.91) without arming a no-op sticky.
            setRailPinned(!!column && height > 0 && column.clientHeight - height > 1);
        };

        measure();
        window.addEventListener('resize', measure);

        // Sticky is in-flow, so arming it cannot change the column's size — the
        // observer settles rather than looping. It is here for the accordion:
        // opening a group changes the rail's height and therefore whether the
        // column still has room above it.
        let observer: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(measure);
            observer.observe(rail);
            if (rail.parentElement) {
                observer.observe(rail.parentElement);
            }
        }

        return () => {
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, []);

    return { railRef, railPinned };
}

/**
 * Individual filter section with collapsible behavior.
 * Shows Plus icon when collapsed, Minus when expanded.
 *
 * Variants:
 * - `card` (stock, PLP + BG): bordered accordion card, §8.1 label-large header.
 * - `rail` (NM SRP, blast-3 "Faceted left rail"): divider-separated groups —
 *   47px group header rows over 1px #D3D3D3 bottom dividers, 14px item type
 *   (--facet-* tokens, tokens/search.css). Same collapsible behavior; only the
 *   frame differs.
 *
 * OPEN STATE IS UNCONTROLLED BY DEFAULT and controlled when — and only when — a
 * caller passes `open`. Under an exclusive accordion the decision belongs to the
 * rail (a section cannot know a sibling just opened), so `CategoryRefinements`
 * hands down the pair; every other mount passes neither and keeps the exact
 * `useState(defaultOpen)` path it had before. `open === undefined` is the
 * discriminator, so a caller must SPREAD NOTHING rather than pass `open={false}`.
 */
/**
 * THE TWO SCREENS OF A DRILL-DOWN FACET LIST. Level 1 is a flat list of group
 * rows, each carrying a trailing chevron; level 2 is one group's values, drawn
 * by the SAME `renderFilterValues` the accordion uses, so no value renderer is
 * duplicated or forked for this shape.
 *
 * MEASURED OFF LIVE, IN THE PAGE, at 390 CSS / DPR 2 on
 * `bergdorfgoodman.com/c/womens-clothing-cat441206` — the takeover opened
 * through the bottom `Filters` pill and read with `getBoundingClientRect` plus
 * `getComputedStyle`, so these are live's resolved values and not a decompiler's
 * reconstruction. Persisted with the screenshots at
 * `.reference/live-bg-filter-drilldown/`.
 *
 *   level 1  `ul.more-filters-content`, first row top 90.08, six `li` at
 *            h = 55 with `border-bottom: 1px solid rgb(225,225,235)`.
 *            Label 13px/19.5px, `letter-spacing: .4px`, Larsseit, padding
 *            `19px 0 17px 15px`. Chevron a 30x30 background-image box at
 *            x = 360 of a 390 pane — flush to the trailing edge — vertically
 *            centred in the row.
 *   level 2  the SAME pane box, `position: fixed`, `z-index: 1010`, padding-top
 *            24px. The header keeps its geometry exactly: control 30x30 at
 *            x = 16, y = 24; title 16px/30.08px, `letter-spacing: .16px`,
 *            uppercase, centred (measured centre 194.99 against a pane centre of
 *            195). Only two things change — the glyph becomes a LEFT chevron and
 *            the title becomes the GROUP'S NAME (`SIZE`).
 *            Value rows h = 50 with NO divider (`border-bottom: 0px none`), a
 *            square checkbox at the leading edge, and labels carrying their
 *            hit count in parentheses.
 *
 * WHAT IS MEASURED ON ONE GROUP ONLY, said out loud rather than generalised:
 * the level-2 capture is live's SIZE screen. Its list carries
 * `class="facet-column recent-sizes-column left-nav-facet-column"` and sits at
 * x = 25 — a 25px inset that the `recent-sizes` class may well own. Designer
 * almost certainly adds an A–Z jump bar and Price a range control. So this
 * component reproduces the PANE CHROME, which is shared by construction because
 * it is the pane's own header, and leaves each group's BODY to the renderer that
 * already draws it. Nothing here invents a second-level treatment per group.
 *
 * FOCUS MOVES WITH THE SCREEN, because a two-level list inside a dialog that
 * left focus behind would strand a keyboard user on a control that no longer
 * exists. Going in, the pane focuses its back control (it owns that element).
 * Coming out, this list focuses the row the shopper left from — `returningFrom`
 * on the context — rather than the top of the pane.
 */
function FacetDrilldownBody({
    drilldown,
    groups,
    renderFilterValues,
}: {
    drilldown: FacetDrilldown;
    groups: ShopperSearch.schemas['ProductSearchRefinement'][];
    renderFilterValues: (
        refinement: ShopperSearch.schemas['ProductSearchRefinement'] & { values: FilterValue[] }
    ) => ReactElement;
}): ReactElement {
    const { group, open, back, returningFrom, clearReturningFrom } = drilldown;
    const rowRefs = useRef(new Map<string, HTMLButtonElement | null>());

    const active = group ? groups.find((g) => g.attributeId === group.attributeId) : undefined;
    const activeHasValues = Boolean(active && Array.isArray(active.values) && active.values.length);

    useEffect(() => {
        if (group || !returningFrom) {
            return;
        }
        rowRefs.current.get(returningFrom)?.focus();
        clearReturningFrom();
    }, [group, returningFrom, clearReturningFrom]);

    /*
     * THE GROUP VANISHING IS A REAL STATE, not a defensive branch. The rail's
     * membership is server-driven and a revalidation lands new refinements under
     * an open screen — applying a Category value can remove the Size group
     * outright.
     *
     * `back()` rather than a local fallback, because the HEADER IS THE PANE'S.
     * Rendering the list here while the pane still painted a back chevron and the
     * departed group's name would leave the two halves disagreeing, and the
     * chevron would be the affordance for a screen that no longer exists — the
     * same invented promise the previous pass declined to paint.
     */
    useEffect(() => {
        if (group && !activeHasValues) {
            back();
        }
    }, [group, activeHasValues, back]);

    if (group && activeHasValues) {
        return (
            /* NO `data-attribute-id` HERE, AND THAT IS THE GATE WORKING. One was
               emitted on this element and on each row, purely so a probe could
               select a known group. `src/__tests__/declared-never-read` caught
               it: unit tests and stories are deliberately NOT consumers, so an
               attribute read only by them is an attribute nothing depends on.
               The tests and the browser harness now key on the row's own
               ACCESSIBLE NAME, which is what a shopper and a screen reader use
               and which cannot rot into a hook nothing reads. */
            <div data-testid="facet-drilldown-values">
                {renderFilterValues(
                    active as ShopperSearch.schemas['ProductSearchRefinement'] & { values: FilterValue[] }
                )}
            </div>
        );
    }

    return (
        <ul data-testid="facet-drilldown-list" className="bg-srp-filter-drill-list">
            {groups.map((refinement) => {
                const attributeId = refinement.attributeId ?? '';
                const label = refinement.label || attributeId;
                return (
                    <li key={attributeId} className="bg-srp-filter-drill-row">
                        <button
                            type="button"
                            ref={(node) => {
                                rowRefs.current.set(attributeId, node);
                            }}
                            data-testid="facet-drilldown-row"
                            /* `detail === 0` is a click SYNTHESISED from Enter or
                               Space; a pointer click always reports 1 or more.
                               It is what tells the pane whether to move focus —
                               see `open` on the drilldown context. */
                            onClick={(event) => open({ attributeId, label }, { viaKeyboard: event.detail === 0 })}
                            className="focus-ring-button bg-srp-filter-drill-row-button flex w-full cursor-pointer items-center justify-between text-left">
                            <span className="bg-srp-filter-drill-row-label">{label}</span>
                            <DrilldownChevron />
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}

/**
 * LIVE'S OWN ROW CHEVRON, PATH AND ALL — unusually, this one does not need
 * reconstructing. Live serves it as a `background-image` data URI on a 30x30
 * box, so the geometry survives the capture intact rather than being inferred
 * from ink. Read identically from two independent sources:
 *
 *   the decompiled 375 search capture, `captures/bg-search-m-filters-375`
 *   the 390 PLP takeover measured in the page, 2026-08-23
 *     (`.reference/live-bg-filter-drilldown/plp-measurements.json`)
 *
 * It is a FILLED two-stroke chevron rather than a stroked one, spanning
 * x 11.0..18.07 and y 8.0..22.14 inside the 30x30 box — about 7 CSS wide, which
 * is the 7.5 the token file's independent ink measurement recorded.
 *
 * `aria-hidden`: the row button's own text already names the group, so the
 * chevron is decoration on top of a label rather than a second one.
 */
function DrilldownChevron(): ReactElement {
    return (
        <svg
            aria-hidden="true"
            focusable="false"
            width="30"
            height="30"
            viewBox="0 0 30 30"
            className="bg-srp-filter-drill-chevron shrink-0">
            <path
                fillRule="evenodd"
                fill="currentColor"
                d="M17.364 15.071 11 21.435l.707.707 6.364-6.364.707-.707L11.708 8 11 8.707l6.364 6.364z"
            />
        </svg>
    );
}

function FilterSection({
    label,
    selectedCount = 0,
    defaultOpen = false,
    variant = 'card',
    open,
    onOpenChange,
    children,
}: {
    label: string;
    /**
     * How many values in this group are currently selected. Rendered as
     * ` (N)` after the label when non-zero, which is what live paints on both
     * brands; zero renders nothing at all rather than `(0)`. Derivation and
     * the two live sources are on `selectedValueCount` above.
     */
    selectedCount?: number;
    defaultOpen?: boolean;
    variant?: 'card' | 'rail';
    open?: boolean;
    onOpenChange?: (next: boolean) => void;
    children: ReactElement;
}): ReactElement {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
    const isControlled = open !== undefined;
    const isOpen = isControlled ? open : uncontrolledOpen;
    const isRail = variant === 'rail';

    const handleOpenChange = useCallback(
        (next: boolean) => {
            if (onOpenChange) {
                onOpenChange(next);
            }
            if (!isControlled) {
                setUncontrolledOpen(next);
            }
        },
        [onOpenChange, isControlled]
    );

    return (
        <section data-testid="refinement-section">
            {/* card: NM accordion (docs/nm-design-system.md §8.1): 1px border,
                label at the 16px/20px/400 label-large role. */}
            <Collapsible
                open={isOpen}
                onOpenChange={handleOpenChange}
                className={isRail ? 'border-b border-(--facet-divider)' : 'border border-border mb-4 rounded-ui'}>
                <h3
                    className={
                        isRail
                            ? 'flex h-(--facet-group-header-height) items-center text-(length:--facet-item-font-size) transition-colors hover:bg-muted/60'
                            : 'type-label-large p-4 transition-colors hover:bg-muted/60'
                    }>
                    <CollapsibleTrigger className="flex items-center justify-between w-full text-left px-1 py-1 -mx-1 cursor-pointer">
                        <span>
                            {label}
                            {selectedCount > 0 && (
                                <span data-testid="refinement-section-count"> ({selectedCount})</span>
                            )}
                        </span>
                        {isOpen ? <Minus className="size-4" /> : <Plus className="size-4" />}
                    </CollapsibleTrigger>
                </h3>
                <CollapsibleContent className={isRail ? 'pb-4 text-(length:--facet-item-font-size)' : 'px-4 pb-4'}>
                    {children}
                </CollapsibleContent>
            </Collapsible>
        </section>
    );
}
